<script setup lang="ts">
import { toRef } from "vue";
import { useDisplay } from "vuetify";
import PatientChatDockTab from "@/components/models/message/chatDock/tabs/PatientChatDockTab.vue";
import InvitesChatDockTab from "@/components/models/message/chatDock/tabs/InvitesChatDockTab.vue";
import { useChatDock, type EmitType } from "@/composables/models/message/useChatDock.ts";
import { useChatDockChrome } from "@/composables/models/message/useChatDockChrome.ts";

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    extend?: boolean;
    onlyPatients?: boolean;
    onlyInvites?: boolean;
  }>(),
  { extend: false }
);

const emit = defineEmits<{
  (e: "update:modelValue", v: boolean): void;
}>();

const { lgAndDown } = useDisplay();
const {
  isDarkMode,
  shellClass,
  controlSurfaceClass,
  closeIconClass,
  dividerColor,
  inputColor,
  tabBtnColor,
  tabBtnClass,
} = useChatDockChrome();

const {
  search,
  activeTab,
  tabs,
  title,
  searchPlaceholder,
  selectedId,
  onSelect: dockOnSelect,
} = useChatDock({
  onlyPatients: toRef(props, "onlyPatients"),
  onlyInvites: toRef(props, "onlyInvites"),
  extend: toRef(props, "extend"),
});

const onSelect = async (item: EmitType) => {
  await dockOnSelect(item);
  emit("update:modelValue", false);
};
</script>

<template>
  <Teleport to="body">
    <v-navigation-drawer
      v-if="lgAndDown"
      :model-value="modelValue"
      location="start"
      temporary
      :border="false"
      :elevation="0"
      width="280"
      class="chat-dock-mobile-drawer tw:z-drawer-layer"
      :class="isDarkMode ? 'chat-dock-mobile-drawer--dark' : 'chat-dock-mobile-drawer--light'"
      data-testid="chat-dock-mobile-drawer"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <div
        class="tw:!pl-2 tw:!pr-4 tw:!pt-[calc(0.5rem_+_env(safe-area-inset-top))] tw:!pb-[calc(0.5rem_+_env(safe-area-inset-bottom))] tw:h-full tw:flex tw:flex-col"
        :class="shellClass"
      >
        <div class="tw:flex tw:items-center tw:justify-between tw:!px-2">
          <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
            <v-icon
              icon="mdi-forum-outline"
              size="18"
              class="tw:shrink-0"
            />
            <div class="tw:text-sm tw:font-extrabold tw:truncate">{{ title }}</div>
          </div>
          <v-btn
            size="small"
            variant="flat"
            class="tw:!rounded-md tw:!min-w-0 tw:!h-8 tw:!w-8 tw:!p-0 tw:shrink-0"
            :class="controlSurfaceClass"
            :aria-label="$t('generic.action.close')"
            @click="emit('update:modelValue', false)"
          >
            <v-icon
              :class="closeIconClass"
              icon="mdi-close"
            />
          </v-btn>
        </div>

        <v-divider
          class="tw:!my-3"
          :color="dividerColor"
        />

        <div class="tw:px-3 tw:pb-2">
          <v-text-field
            v-model="search"
            :color="inputColor"
            density="compact"
            variant="plain"
            hide-details
            :placeholder="searchPlaceholder"
            flat
            class="tw:!text-xs tw:!rounded-md tw:!px-2 tw:!pb-1.5"
            :class="controlSurfaceClass"
            prepend-inner-icon="mdi-magnify"
          />

          <v-btn-group
            v-if="!onlyPatients"
            class="tw:!h-8 tw:w-full tw:shrink-0 tw:!mt-2 tw:rounded-none"
          >
            <v-btn
              v-for="tab in tabs"
              :key="tab.key"
              :disabled="tab.disabled"
              :color="tabBtnColor(activeTab === tab.key)"
              class="tw:!flex-1 tw:!min-w-0 tw:!rounded-md"
              :class="tabBtnClass(activeTab === tab.key)"
              density="compact"
              variant="flat"
              @click="activeTab = tab.key"
            >
              <span
                class="tw:text-xs tw:flex tw:items-center tw:justify-center tw:gap-1 tw:min-w-0 tw:truncate"
              >
                <v-icon
                  :icon="tab.icon"
                  class="tw:shrink-0"
                />
                <span class="tw:truncate">{{ tab.name }}</span>
              </span>
            </v-btn>
          </v-btn-group>
        </div>

        <v-divider
          class="tw:!my-3"
          :color="dividerColor"
        />

        <div
          class="tw:grow tw:min-h-0 tw:overflow-y-auto tw:overflow-x-hidden tw:!pt-1 tw:px-2 tw:pb-2"
        >
          <patient-chat-dock-tab
            v-show="activeTab === 'patients'"
            :active="activeTab === 'patients'"
            :search="search"
            :collapsed="false"
            :selected-id="selectedId"
            @select="onSelect"
          />

          <invites-chat-dock-tab
            v-show="activeTab === 'invites'"
            :active="activeTab === 'invites'"
            :search="search"
            :collapsed="false"
            :selected-id="selectedId"
            @select="onSelect"
          />
        </div>
      </div>
    </v-navigation-drawer>
  </Teleport>
</template>

<style>
.chat-dock-mobile-drawer.v-navigation-drawer {
  top: 0 !important;
  height: 100dvh !important;
  max-height: 100dvh !important;
  background: transparent !important;
  border: none !important;
  border-right-width: 0 !important;
}

.chat-dock-mobile-drawer .v-navigation-drawer__content {
  background: transparent !important;
}

.chat-dock-mobile-drawer.v-navigation-drawer,
.chat-dock-mobile-drawer.v-navigation-drawer--temporary.v-navigation-drawer--active {
  box-shadow: none !important;
}

.chat-dock-mobile-drawer--light.v-navigation-drawer,
.chat-dock-mobile-drawer--light.v-navigation-drawer--temporary.v-navigation-drawer--active {
  border-right: thin solid rgba(255, 255, 255, 0.24) !important;
}

.chat-dock-mobile-drawer--dark.v-navigation-drawer,
.chat-dock-mobile-drawer--dark.v-navigation-drawer--temporary.v-navigation-drawer--active {
  border-right: thin solid rgba(var(--v-theme-on-surface), 0.1) !important;
}
</style>
